import { useCallback, useEffect, useState, useRef, useMemo, ReactNode } from 'react';
import Box from '@mui/material/Box';
import type { SxProps } from '@mui/system';
import * as Yup from 'yup';
import { FormikErrors } from 'formik';
import { Button, Checkbox, Input, Alert } from '@/shared/ui';
import { OnboardingController } from '@/shared/endpoints/onboarding';
import AutoAddress from '@/modules/onboarding/pages/about-you/address/components/AutoAddress';
import ManualAddressFormElements from '../manual-address-form-elements';
import { AddressValidation } from './types';
import { ExtendedAddress } from './types';
import { Address } from '@/shared/endpoints/onboarding/models/response';
import { AddressTemplate } from './components/AddressTemplate';
import { ICustomersUserAddressesResponse } from '../../endpoints/customers';
import { useHeaderHeight } from '@/shared/components/header-height-context';

import { onlyCharsAndNumbers, charLimitBetweenFourAndTwenty } from '../../constants/regex';
import { toast } from 'react-toastify';
import ValidAddressFormikValues from './components/ValidAddressFormikValues';
import { useFormikWrapper } from '@/shared/hooks/formikWrapper';


const basicAddressNameErrorMessage = 'Please enter a name for this address.'
const labelSchema = Yup.string()
    .trim()
    .required(basicAddressNameErrorMessage)
    .matches(charLimitBetweenFourAndTwenty, 'Address Name must be between 4 and 20 characters.')
    .matches(onlyCharsAndNumbers, 'Address Name can only contain letters and numbers.');

const validationSchema = Yup.object({
    addressLine1: Yup.string().trim().required('Please enter a street address.'),
    city: Yup.string().trim().required('Please enter a city.'),
    state: Yup.string().trim().length(2, 'State must be exactly 2 characters.').required('Please enter a state.'),
    zip: Yup.string().test('len', 'Please enter a valid zip code', val => {
        return val?.toString().length === 5;
    }),
    label: labelSchema,
    unit: Yup.string().test('required-if-needsUnit', 'Please enter a valid apartment/Suite/Box Number.', function () {
        const { needsUnit, unit } = this.parent;
        return !needsUnit || Boolean(unit);
    })
});

const selectedAddressDefaultValue: ExtendedAddress = {
    fullAddress: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
    unit: '',
    verified: false,
    needsUnit: false,
    label: '',
    preferred: false
};

const capitalizeWords = (text: string) => {
    if (!text) return '';
    return text
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

export interface IProps {
    loading?: boolean;
    autoDefault?: boolean;
    manualFormTitle?: string;
    visibleBackToAuto?: boolean;
    withDefaultCheckbox?: boolean;
    withManualAddressDemo?: boolean;
    withPreviewWhenNeedsUnit?: boolean;
    withAutoAddressPreview?: boolean;
    defaultLabelAndHidden?: string;
    addressLabelHeader?: string;
    addressFieldPlaceholder?: string;
    feValidation?: { field: string; message: string } | null;
    formVariant?: AddressFormVariant;
    defaultValues?: Partial<ICustomersUserAddressesResponse>;
    onSubmit?: (address: ExtendedAddress, config: { noApiCall?: boolean, skipNavOnSave?: boolean }) => void | Promise<void>;
    handleEdit?: () => void;
    onGoBack?: () => void;
    resetFeValidation?: () => void;
    className?: string;
    contentClassName?: string;
    formSx?: SxProps;
    hasCancel?: boolean;
    onCancel?: (isDirty: boolean) => void;
    hasEditWhenManual?: boolean;
    previewTheme?: 'default' | 'green';
    previewTitle?: string;
    previewTitleClassName?: string;
    resetOnBack?: boolean;
    addressViewingSecondButtonText?: string;
    nonDefaultAddressViewingSecondButtonText?: string;
    nonDefaultAddressPrimaryButtonText?: string;
    secondaryButtonVariant?: 'text' | 'outlined';
    defaultAddressValidConfirmText?: string;
    feValidationInvoke: (values: ExtendedAddress) => boolean;
    autoValidSkipApiCall?: boolean;
    modal?: boolean;
    endIconSaveAndContinueVisible?: boolean;
    avoidIndividualErrorMessage?: boolean;
    previewFooterBanner?: ReactNode;
}
const basicFailure = 'Unable to verify address. Please review and make sure you are entering a valid address.';
const unableToUseAutoCompleteError = 'Unable to verify address. Please enter your address manually.';
const addressVerificationFailedError = 'Address verification failed. Please provide more details.';

const getFullAddressString = (address: Address) => {
    const { streetAddress, city, state, ZIPCode } = address;

    let fullAddress = '';

    if (streetAddress) fullAddress += capitalizeWords(streetAddress);
    if (city) fullAddress += `${fullAddress ? ', ' : ''}${capitalizeWords(city)}`;
    if (state) fullAddress += `${fullAddress ? ', ' : ''}${state.toUpperCase()}`;
    if (ZIPCode) fullAddress += `${fullAddress ? ' ' : ''}${ZIPCode}`;

    return fullAddress;
};

export type AddressFormVariant = 'auto' | 'manual';

const MainAddressForm = ({
    autoDefault = false,
    previewTheme = 'default',
    loading: propLoading,
    manualFormTitle = '',
    visibleBackToAuto,
    withDefaultCheckbox,
    defaultValues = {},
    formVariant: propFormVariant = 'auto',
    defaultLabelAndHidden = '',
    hasEditWhenManual,
    withManualAddressDemo,
    withAutoAddressPreview,
    withPreviewWhenNeedsUnit,
    previewTitle,
    previewTitleClassName,
    onSubmit,
    onGoBack,
    feValidation,
    resetFeValidation,
    className = '',
    contentClassName = '',
    formSx = {},
    hasCancel = false,
    resetOnBack,
    addressLabelHeader = 'Address Label',
    addressFieldPlaceholder = 'ex. Home',
    onCancel = () => { },
    addressViewingSecondButtonText,
    nonDefaultAddressViewingSecondButtonText,
    nonDefaultAddressPrimaryButtonText,
    secondaryButtonVariant = 'text',
    defaultAddressValidConfirmText = '',
    autoValidSkipApiCall = false,
    modal,
    feValidationInvoke = () => { return true },
    endIconSaveAndContinueVisible = true,
    avoidIndividualErrorMessage = false,
    previewFooterBanner
}: IProps) => {
    const [loading, setLoading] = useState(false);
    const [unitNeeded, setUnitNeeded] = useState(false);
    const [uniqueKey, setUniqueKey] = useState(0);
    const [previewWhenUnitNeeded, setPreviewWhenUnitNeeded] = useState(false);
    const [defaultAddressValid, setDefaultAddressValid] = useState(Object.keys(defaultValues).length ? 'initial_valid_preview' : '');
    const [selectedAddressState, setSelectedAddressState] = useState<AddressValidation>({ needsUnit: false, error: '' });
    const [formVariant, setFormVariant] = useState<AddressFormVariant>(propFormVariant);
    const headerHeight = useHeaderHeight();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [newValuesForSubmit, setNewValuesForSubmit] = useState<ExtendedAddress | null>(null);

    const formik = useFormikWrapper<ExtendedAddress>({
        validationSchema,
        initialValues: {
            ...selectedAddressDefaultValue,
            ...defaultValues,
            preferred: autoDefault,
            label: defaultLabelAndHidden
        } as ExtendedAddress,
        onSubmit: (values) => {
            const upperState = values.state?.toUpperCase() ?? '';
            formik.setFieldValue('state', upperState, false); // update form without revalidating immediately
            handleVerifyAddress({
                ...values,
                state: upperState
            });
        }
    });

    useEffect(() => {
        resetFeValidation?.();
    }, [formik.values.label])


    const getAddressDefaults = useCallback(() => {
        return {
            ...selectedAddressDefaultValue,
            label: formik.values.label || '',
            preferred: autoDefault || formik.values.preferred
        };
    }, [autoDefault, formik.values.label, formik.values.preferred]);

    const handleManualInput = useCallback((keepError = false, resetToDefaults = true) => {
        resetToDefaults && formik.setValues(getAddressDefaults());
        formik.setTouched({});
        setFormVariant('manual');
        setUnitNeeded(false);
        formik.setFieldValue('needsUnit', false);
        !keepError && setSelectedAddressState({ needsUnit: false, error: '' });
    }, [formik, getAddressDefaults]);

    const handleVerifyAddress = useCallback(
        async (values: ExtendedAddress) => {
            setLoading(true);
            const address = {
                addressLine2: values.addressLine1 || '',
                addressLine1: values.unit || '',
                city: values.city,
                state: values.state,
                zip: values.zip
            };

            try {
                const res = await OnboardingController.validateAddress(address);
                if (res?.error || (res.corrections || []).some(correction => correction.text)) {
                    if (res?.error) {
                        setSelectedAddressState({ needsUnit: false, error: formVariant === 'manual' ? basicFailure : unableToUseAutoCompleteError });
                        setFormVariant('manual');
                        handleManualInput(true, formVariant !== 'manual');
                    } else {
                        const needsUnit = (res.corrections || []).some(correction => correction.code === '32' && correction.text);
                        if (values.unit && needsUnit) {
                            formik.setFieldError('unit', 'Please enter a valid unit');
                        } else { // this is not just a needs unit case.... 
                            // the needs unit without having added the unit yet case but also this if catches other errors where there is corrections
                            setSelectedAddressState({ needsUnit, error: needsUnit ? addressVerificationFailedError : basicFailure });
                            setUnitNeeded(needsUnit);
                            needsUnit && withPreviewWhenNeedsUnit && setPreviewWhenUnitNeeded(true);
                            formik.setFieldValue('needsUnit', needsUnit);
                            formik.setTouched({ needsUnit: false });
                        }
                    }
                } else {
                    const fullAddress = getFullAddressString(res.address!);
                    formik.setTouched({});
                    // in the case of an automatic address, we need to validate the label to prevent submitting a form                    
                    try {
                        await labelSchema.validate(values.label);
                    } catch (error) {
                        formik.setFieldError('label', basicAddressNameErrorMessage);
                        return;
                    }
                    const newValues: ExtendedAddress = {
                        ...values,
                        addressLine1: res?.address?.streetAddress || '',
                        addressLine2: '',
                        city: res?.address?.city || '',
                        state: res?.address?.state || '',
                        zip: res?.address?.ZIPCode || '',
                        unit: res?.address?.secondaryAddress || '',
                        fullAddress,
                        verified: true,
                        preferred: values.preferred,
                        label: values.label,
                        coordinates: {
                            lat: '',
                            lng: ''
                        }
                    };

                    const validationRes = feValidationInvoke(newValues);
                    setNewValuesForSubmit(newValues);
                    if (formVariant === 'auto') {
                        if (validationRes) {
                            if (withAutoAddressPreview) {
                                setDefaultAddressValid('manual_valid_preview');
                            } else {
                                setDefaultAddressValid('auto_valid_preview');
                                await onSubmit?.(newValues, { noApiCall: autoValidSkipApiCall, skipNavOnSave: true });
                            }
                        }
                    } else if (formVariant === 'manual') {
                        if (validationRes) setDefaultAddressValid('manual_valid_preview');
                        handleAddressSelection(newValues, false);
                    }
                }
                // }
            } catch (error) {
                toast.error('Unable to validate address. Please try again.');
                console.error(error);
            } finally {
                setLoading(false);
                setUniqueKey(prevKey => prevKey + 1);
            }
        },
        [handleManualInput, formik, onSubmit, formVariant, withPreviewWhenNeedsUnit, withManualAddressDemo]
    );

    const handleAddressSelection = useCallback(
        (address: ExtendedAddress, autoVerify = true) => {
            const entries = Object.entries(address);
            const promises: Promise<void> | Promise<FormikErrors<ExtendedAddress>>[] = [];
            entries.forEach(([key, value]) => {
                promises.push(Promise.resolve(formik.setFieldValue(key, value, false) as Promise<FormikErrors<ExtendedAddress>>));
            });
            Promise.all(promises).then(() => {
                autoVerify && handleVerifyAddress({ ...address, preferred: formik.values.preferred, label: formik.values.label!.trim() });
            });
        },
        [formik, handleVerifyAddress]
    );

    const handleAdditionalAddressChange = useCallback(
        (value: string) => {
            resetFeValidation?.();
            formik.setValues(prevValues => ({
                ...prevValues,
                verified: false,
                unit: value
            }));
        },
        [formik, resetFeValidation]
    );

    const handleAddressInputEvent = useCallback(() => {
        setSelectedAddressState({ needsUnit: false, error: '' });
        formik.setValues(getAddressDefaults());
        setUnitNeeded(false);
        formik.setFieldTouched('needsUnit', false);
        formik.setFieldValue('needsUnit', false);
        resetFeValidation?.();
    }, [formik, getAddressDefaults, resetFeValidation]);

    const handleManualFieldChange = useCallback(
        (field: string, value: string) => {
            resetFeValidation?.();
            if (field !== 'unit') {
                formik.setValues({
                    ...formik.values,
                    [field]: value,
                    unit: '',
                    needsUnit: false
                });
                setUnitNeeded(false);
                setSelectedAddressState(() => ({
                    error: '',
                    needsUnit: false
                }));
            } else {
                formik.setFieldValue(field, value);
            }
        },
        [formik, resetFeValidation]
    );

    const handleBackToAuto = useCallback(() => {
        setFormVariant('auto');
        setDefaultAddressValid('');
        resetFeValidation?.();
        formik.setValues(getAddressDefaults());
        setUnitNeeded(false);
        setSelectedAddressState(() => ({
            error: '',
            needsUnit: false
        }));
        formik.setTouched({});
    }, [formik, getAddressDefaults]);

    const handleTryAgain = useCallback(() => {
        if (feValidation?.field === 'address' || !feValidation) {
            formik.setValues(getAddressDefaults());
            formik.setTouched({});
            setUnitNeeded(false);
            setDefaultAddressValid('');
            setSelectedAddressState(() => ({
                error: '',
                needsUnit: false
            }));
        }
    }, [feValidation, formik, getAddressDefaults]);

    const handleEdit = () => {
        setDefaultAddressValid('');
    };


    const checkForErrorsAndScroll = useCallback(() => {
        const element = scrollContainerRef.current?.querySelector(`.alert__error, .border-red-500`);
        const scrollContainer = scrollContainerRef.current;

        if (!element || !scrollContainer) return;

        const elementRect = element.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();

        const isVisible =
            elementRect.top >= containerRect.top &&
            elementRect.bottom <= containerRect.bottom;

        if (isVisible) return;

        const elementOffsetTop = element.offsetTop;

        setTimeout(() => {
            scrollContainer.scrollTo({
                top: elementOffsetTop - headerHeight - 200,
                behavior: 'smooth',
            });
        }, 200);
    }, [headerHeight]);

    const onSubmitClick = useCallback((e: { preventDefault: () => void; }) => {
        e.preventDefault();
        formik.handleSubmit();
        setTimeout(() => {
            checkForErrorsAndScroll();
        }, 400);
    }, [checkForErrorsAndScroll, formik])


    const headingUi = useMemo(() => {
        if (formVariant === 'auto' || defaultAddressValid) {
            return (
                <div className={`mb-1 flex flex-col gap-1 ${modal ? 'items-center' : ''}`}>
                    {!modal && <div className={`flex items-center gap-2`}>
                        <span className="text-lg font-bold">Shipping Address</span>
                         {true && <span className="text-red-500">*</span>}
                    </div>}
                    {defaultAddressValid === 'manual_valid_preview' && (
                        <div className="text-md">Here is what we found. Is this correct?</div>
                    )}
                </div>
            )
        } else if (modal) {
            return null;
        } else {
            return (
                <div className="mb-1 flex flex-col gap-1">
                    <div className={`flex items-center gap-2`}>
                        <span className="text-lg font-bold">Enter your shipping address</span>
                    </div>
                </div>
            )
        }
    }, [defaultAddressValid, formVariant, modal]);

    const moreThanOneFieldTouchedAndInsistShowSingleError = useMemo(() => {
        const errorFields = Object.keys(formik.errors);
        const touchedFields = Object.keys(formik.touched);
        const fieldsWithErrorsAndTouched = errorFields.filter(field => touchedFields.includes(field));
        return fieldsWithErrorsAndTouched.length > 1 && avoidIndividualErrorMessage && formVariant === 'manual';
    }, [formik.errors, formik.touched, avoidIndividualErrorMessage, formVariant]);

    return (
        <div className={`flex flex-col h-full ${className}`} data-testid="MainAddressForm">
            <Box component="form" sx={{ ...formSx, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {headingUi}
                {defaultAddressValid ? (
                    <AddressTemplate
                        addressViewingSecondButtonText={addressViewingSecondButtonText}
                        nonDefaultAddressViewingSecondButtonText={nonDefaultAddressViewingSecondButtonText}
                        title={previewTitle}
                        titleClassName={previewTitleClassName}
                        theme={previewTheme}
                        address={formik.values}
                        formVariant={formVariant}
                        hasEditWhenManual={hasEditWhenManual}
                        onSubmit={() => {
                            onSubmit?.(newValuesForSubmit!, {
                                noApiCall: defaultAddressValid === 'initial_valid_preview',
                                skipNavOnSave: false
                            });
                        }}
                        defaultAddressValid={defaultAddressValid}
                        onEdit={handleEdit}
                        loading={loading || propLoading}
                        onGoBack={resetOnBack ? () => setDefaultAddressValid('') : onGoBack}
                        tryAgainHandler={handleTryAgain}
                        secondaryButtonVariant={secondaryButtonVariant}
                        nonDefaultAddressPrimaryButtonText={nonDefaultAddressPrimaryButtonText}
                        defaultAddressValidConfirmText={defaultAddressValidConfirmText}
                        modal={modal}
                        previewFooterBanner={previewFooterBanner}
                    />
                ) : (
                    <div className={`flex flex-col justify-between h-full flex-1 relative ${contentClassName}`}>
                        <div className={`flex flex-col gap-2`} ref={scrollContainerRef}>
                            {unitNeeded && previewWhenUnitNeeded && formVariant !== 'manual' && (
                                <ValidAddressFormikValues
                                    theme="green"
                                    values={formik.values}
                                    hasEdit
                                    onEditPressed={() => setPreviewWhenUnitNeeded(false)}
                                />
                            )}

                            {formVariant === 'manual' ? (
                                <ManualAddressFormElements
                                    formTitle={manualFormTitle}
                                    handleChange={handleManualFieldChange}
                                    values={formik.values}
                                    errors={formik.errors}
                                    touched={formik.touched}
                                    selectedAddressState={selectedAddressState}
                                    error={
                                        !selectedAddressState.needsUnit &&
                                            selectedAddressState.error &&
                                            selectedAddressState.error === unableToUseAutoCompleteError
                                            ? selectedAddressState.error
                                            : ''
                                    }
                                    showApartmentSuiteBoxNumberField={unitNeeded}
                                    moreThanOneFieldTouchedAndInsistShowSingleError={moreThanOneFieldTouchedAndInsistShowSingleError}
                                />
                            ) : (
                                <AutoAddress
                                    key={uniqueKey}
                                    values={formik.values}
                                    touched={formik.touched}
                                    errors={formik.errors}
                                    needsUnit={unitNeeded}
                                    hideAutocomplete={unitNeeded && previewWhenUnitNeeded}
                                    handleBlur={() => { }}
                                    handleManualInput={handleManualInput}
                                    onAddressSelection={handleAddressSelection}
                                    onAdditionalAddressChange={handleAdditionalAddressChange}
                                    onAddressInputEvent={handleAddressInputEvent}
                                    alertClassName='!mt-[10px]'
                                />
                            )}
                            {!defaultLabelAndHidden && <Input
                                label={addressLabelHeader}
                                placeholder={addressFieldPlaceholder}
                                name="label"
                                type="text"
                                alertClassName='!mt-[10px]'
                                value={formik.values.label}
                                error={formik.touched.label && Boolean(formik.errors.label)}
                                errorMessage={formik.errors.label}
                                onChange={formik.handleChange}
                                required
                                testId="address-label"
                                labelToReflectError
                                showErrorMessage={!moreThanOneFieldTouchedAndInsistShowSingleError}
                            />}
                            {withDefaultCheckbox && (
                                <Checkbox
                                    label={
                                        <span className="relative top-0 font-atkinsonHyperlegible text-base md:text-lg">
                                            Make this default shipping address
                                        </span>
                                    }
                                    name="preferred"
                                    checked={formik.values.preferred}
                                    onChange={formik.handleChange}
                                />
                            )}
                            {moreThanOneFieldTouchedAndInsistShowSingleError && <Alert className='mt-[10px] font-dmSans' label={'Make sure all fields are filled out correctly.'} />}
                        </div>
                        <div className='flex w-full flex-col'>
                            {(feValidation || selectedAddressState.error === basicFailure) && <Alert className="mt-2.5 font-dmSans lg:mt-6" label={feValidation?.message || selectedAddressState.error} />}
                            <Button
                                // data-testid="save" TODO: find after
                                type="submit"
                                variant="contained"
                                color="primary"
                                showEndIcon={endIconSaveAndContinueVisible}
                                className="mt-6"
                                loading={loading || propLoading}
                                testId="button-address-form-continue"
                                onClick={onSubmitClick}
                            >
                                Save & Continue
                            </Button>

                            {(visibleBackToAuto || formVariant === 'manual') && (
                                <Button
                                    sx={{ width: '100%', mt: 2 }}
                                    variant="outlined"
                                    disabled={loading || propLoading}
                                    onClick={formVariant === 'manual' ? handleBackToAuto : onGoBack}
                                    testId="button-address-form-back"
                                >
                                    Back
                                </Button>
                            )}
                            {hasCancel && (
                                <div className='mt-[25px]'>
                                    <Button
                                        testId="button-select-default-card-continue"
                                        variant="text"
                                        sx={{ width: '100%', fontSize: '1.125rem' }}
                                        onClick={() => {
                                            onCancel?.(formik.dirty);
                                        }}
                                    >
                                        <span className="˝font-dmSansMedium font-bold">Cancel</span>
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </Box>
        </div>
    );
};

export default MainAddressForm;
